import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from 'semantic-ui-react';

import { useProjectContext } from '~/hooks';
import { useProjectWorksheetContext } from '~/features/projectWizard/worksheets/project/providers/ProjectWorksheetProvider';
import { projectWorksheetSearchIndex, WorksheetSearchEntry } from '~/features/projectWizard/worksheets/project/searchIndex';
import './WorksheetSearch.scss';

const MAX_RESULTS = 25;

interface ResolvedEntry extends WorksheetSearchEntry {
  resolvedLabel: string;
  sectionLabelText: string;
  subsectionLabelText: string;
}

export default function WorksheetSearch() {
  const { config, expandSection } = useProjectWorksheetContext();
  const {
    conditions: {
      isFieldDisplaying,
      isSubsectionDisplaying,
      isSectionDisplaying,
      fieldLabel,
      sectionLabel,
      subsectionLabel
    }
  } = useProjectContext();

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const sectionLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    config.forEach(section => {
      map.set(section.id, sectionLabel(section.id, section.label));
      section.subsections.forEach(sub => {
        map.set(`${section.id}::${sub.id}`, subsectionLabel(sub.id, sub.label));
      });
    });
    return map;
  }, [config, sectionLabel, subsectionLabel]);

  const resolvedEntries = useMemo<ResolvedEntry[]>(() => {
    return projectWorksheetSearchIndex
      .filter(entry => isSectionDisplaying(entry.sectionId))
      .filter(entry => isSubsectionDisplaying(entry.subsectionId))
      .filter(entry => isFieldDisplaying(entry.schemaKey))
      .map(entry => ({
        ...entry,
        resolvedLabel: fieldLabel(entry.schemaKey, entry.label),
        sectionLabelText: sectionLabelMap.get(entry.sectionId) ?? entry.sectionId,
        subsectionLabelText: sectionLabelMap.get(`${entry.sectionId}::${entry.subsectionId}`) ?? entry.subsectionId
      }));
  }, [isSectionDisplaying, isSubsectionDisplaying, isFieldDisplaying, fieldLabel, sectionLabelMap]);

  const results = useMemo<ResolvedEntry[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return resolvedEntries
      .filter(entry => {
        const haystacks = [
          entry.resolvedLabel,
          entry.label,
          entry.sectionLabelText,
          entry.subsectionLabelText,
          entry.schemaKey,
          ...(entry.aliases ?? [])
        ];
        return haystacks.some(h => h.toLowerCase().includes(q));
      })
      .slice(0, MAX_RESULTS);
  }, [query, resolvedEntries]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (!isOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isOpen]);

  const selectEntry = (entry: ResolvedEntry) => {
    expandSection(entry.sectionId);
    setIsOpen(false);
    setQuery('');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const fieldEl = document.getElementById(`field-${entry.schemaKey}`);
        const target = fieldEl ?? document.getElementById(`${entry.sectionId}-${entry.subsectionId}`);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const entry = results[activeIndex];
      if (entry) selectEntry(entry);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const showDropdown = isOpen && query.trim().length > 0;

  return (
    <div className="worksheet-search" ref={wrapperRef}>
      <Input
        icon="search"
        iconPosition="left"
        fluid
        placeholder="Search worksheet fields..."
        value={query}
        onChange={(_, { value }) => {
          setQuery(value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={onKeyDown}
      />
      {showDropdown && (
        <div className="worksheet-search__results" role="listbox">
          {results.length === 0 ? (
            <div className="worksheet-search__empty">No matching fields</div>
          ) : (
            results.map((entry, i) => (
              <button
                key={`${entry.sectionId}-${entry.subsectionId}-${entry.schemaKey}`}
                type="button"
                role="option"
                aria-selected={i === activeIndex}
                className={`worksheet-search__result${i === activeIndex ? ' is-active' : ''}`}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectEntry(entry)}
              >
                <div className="worksheet-search__breadcrumb">
                  {entry.sectionLabelText}
                  <span aria-hidden="true"> › </span>
                  {entry.subsectionLabelText}
                </div>
                <div className="worksheet-search__label">{entry.resolvedLabel}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
